import React, { useState } from "react";
import { Modal, Pagination } from "@mui/material";

import styles from "components/helpers/Table/Table.module.css";
import { useCodeTableData } from "hooks/useCodeTableData";

const CodeTableDataModal = ({
  isOpen,
  onClose,
  codeTableId,
  codeTableDescription,
}) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: codeTableData, isLoading } = useCodeTableData({
    page,
    rows: 20,
    codeTableId,
    searchTerm: searchTerm || null,
    enabled: isOpen && !!codeTableId,
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
  };

  const codeTableDataList = codeTableData?.data || [];
  const totalRecords = codeTableData?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / 20);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="code-table-data-modal"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          width: "1000px",
          overflow: "auto",
          outline: "none",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <h3
            style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "600" }}
          >
            {codeTableDescription || `Code Table ${codeTableId}`}
          </h3>
          <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
            Code Table Data
          </p>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginBottom: "20px",
            gap: "12px",
          }}
        >
          <input
            type="text"
            placeholder="Search code table data..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              padding: "10px 14px",
              border: "1px solid #d0d5dd",
              borderRadius: "6px",
              fontSize: "14px",
              width: "250px",
              outline: "none",
            }}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              style={{
                padding: "6px 12px",
                backgroundColor: "#f3f4f6",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                color: "#374151",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Clear
            </button>
          )}
        </div>

        {isLoading && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div>Loading code table data...</div>
          </div>
        )}

        {!isLoading && (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th>Parent ID</th>
                    <th>Code Data Code</th>
                    <th>Code Data Description</th>
                    <th>Code System Code</th>
                    <th>Code System Description</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {codeTableDataList.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#6b7280",
                        }}
                      >
                        {searchTerm
                          ? `No data found matching "${searchTerm}". Try a different search term.`
                          : "No data found for this code table."}
                      </td>
                    </tr>
                  ) : (
                    codeTableDataList.map((dataItem, index) => (
                      <tr key={dataItem.id || index}>
                        <td>{dataItem.parentId || "-"}</td>
                        <td>{dataItem.codeDataCode || "-"}</td>
                        <td>{dataItem.codeDataDescription || "-"}</td>
                        <td>{dataItem.codeSystemCode || "-"}</td>
                        <td>{dataItem.codeSystemDescription || "-"}</td>
                        <td>
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                              backgroundColor: dataItem.active
                                ? "#ecfdf5"
                                : "#fef2f2",
                              color: dataItem.active ? "#059669" : "#dc2626",
                            }}
                          >
                            {dataItem.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: "1px solid var(--gray-200)",
                  padding: "12px 16px",
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "16px",
                  backgroundColor: "#fff",
                  borderBottomLeftRadius: "12px",
                  borderBottomRightRadius: "12px",
                }}
              >
                <span style={{ margin: 0, color: "#666", fontSize: "12px" }}>
                  {(page - 1) * 20 + 1}-{Math.min(page * 20, totalRecords)} of{" "}
                  {totalRecords} results
                </span>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_event, value) => setPage(value)}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontSize: "12px",
                        minWidth: "24px",
                        height: "24px",
                        margin: "0 1px",
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
            borderTop: "1px solid #d9d8df",
            paddingTop: "20px",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "12px 24px",
              backgroundColor: "#f3f4f6",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              color: "#374151",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export { CodeTableDataModal };
